/** @jsxImportSource @emotion/react */
import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { SubTitleLayout, SubTitleGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import _ from 'lodash';
import CustomGrid from 'components/grids/CustomGrid';
import { findMdIvesArticlePopupList } from 'apis/md/MdProject';
import { MdIvesArticle } from 'models/md/MdIves';

type Props = {
  open: boolean;
  close: () => void;
  ids: string[];
  condition: { elmPrjId: string; ivesMdlId?: string };
  callbackFn: (selectedItems: MdIvesArticle[]) => void;
};

/**
 * 투자항목 추가 팝업
 */
const IvesInqPopup = ({ open, close, callbackFn, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();

  const gridRef = useRef<any>();
  const [rowData, setRowData] = useState<MdIvesArticle[]>([]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'prdnProcCdNm',
        header: String(t('com.label.공정', '공정')),
        minWidth: 70,
        width: 100,
        align: 'center',
      },
      {
        binding: 'eqclNm',
        header: String(t('com.label.설비군', '설비군')),
        minWidth: 70,
        width: 120,
        align: 'center',
      },
      {
        binding: 'ivesAtcNm',
        header: String(t('com.label.투자항목', '투자항목')),
        minWidth: 70,
        width: '*',
        align: 'center',
      },
      {
        binding: 'cellDivsCdNm',
        header: String(t('com.label.셀 구분', '셀 구분')),
        minWidth: 70,
        width: '*',
        align: 'center',
      },
      {
        header: '수량기준',
        columns: [
          {
            binding: 'qtyUnitCdNm',
            header: String(t('com.label.기준', '기준')),
            minWidth: 70,
            width: '*',
            align: 'center',
          },
          {
            binding: 'msrScf',
            header: String(t('com.label.배수', '배수')),
            minWidth: 70,
            width: 100,
            align: 'center',
          },
          {
            binding: 'addQty',
            header: String(t('com.label.추가', '추가')),
            minWidth: 70,
            width: 100,
            align: 'center',
          },
          {
            binding: 'calLogcCtn',
            header: String(t('com.label.계산로직', '계산로직')),
            minWidth: 70,
            width: '*',
            align: 'center',
          },
        ],
      },
      {
        binding: 'uprcDcnTpCdNm',
        header: String(t('com.label.Ref 기준', 'Ref 기준')),
        minWidth: 70,
        width: '*',
        align: 'center',
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      if (e.panel === s.rowHeaders && e.col === 0) {
        const item = s.rows[e.row].dataItem;

        if (props.ids.includes(`${item.prdnProcCd}_${item.eqclId}_${item.ivesAtcId}`)) {
          e.cell.removeChild(e.cell.firstChild);
        }
      }
    });
  };

  const onBeginningEdit = (s, e) => {
    const item = s.rows[e.row].dataItem;
    if (props.ids.includes(`${item.prdnProcCd}_${item.eqclId}_${item.ivesAtcId}`)) {
      // 숨긴 행과 동일한 조건
      e.cancel = true; // 편집 및 선택 방지
    }
  };

  const btnSearch = () => {
    // search
    openLoading(true);
    findMdIvesArticlePopupList(props.condition)
      .then((res) => {
        setRowData(res || []);
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.데이터 조회 중 오류가 발생했습니다.',
            '데이터 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnSave = () => {
    const selectedItems: MdIvesArticle[] = gridRef.current.rows
      .filter((row) => row.isSelected)
      .map((row) => row.dataItem);
    if (selectedItems.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    callbackFn(selectedItems);

    close();
  };

  useEffect(() => {
    btnSearch();
  }, []);

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={btnSave}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={String(t('md.label.투자항목 리스트 조회', '투자항목 리스트 조회'))}
      size={'lg'}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.투자항목 목록', '투자항목 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={500}
        isReadOnly={true}
        allowPinning={false}
        align="center"
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
      />
    </CustomDialog>
  );
};

export default IvesInqPopup;
